/**
 * Hand-category evaluation for combined hand (hole cards + board).
 *
 * Per /design/specs/5_game_engine_spec.md §9: V1 uses a combinatoric best-5-of-7
 * evaluator. This module returns only the category (set / pair / flush / etc.) —
 * full hand ranking (kicker comparisons for showdown) comes with the state
 * machine in a later subslice.
 *
 * The wheel (A-2-3-4-5) is explicitly NOT counted as a straight per game
 * engine spec §1.1's "no ace-low usage in V1." This is a deliberate V1
 * limitation.
 */

import { type Card, type Suit } from '@/types/card';
import {
  STRAIGHTS_BY_HIGH,
  rankCounts,
  straightRanksFor,
  suitCounts,
} from './handEvaluator';
import { RANK_VALUE } from '@/types/card';

export type HandCategory =
  | 'StraightFlush'
  | 'Quads'
  | 'FullHouse'
  | 'Flush'
  | 'Straight'
  | 'Set'
  | 'TwoPair'
  | 'OnePair'
  | 'HighCard';

/** Category ranking — higher = stronger. Used for category comparison. */
export const CATEGORY_STRENGTH: Record<HandCategory, number> = {
  StraightFlush: 9,
  Quads: 8,
  FullHouse: 7,
  Flush: 6,
  Straight: 5,
  Set: 4,
  TwoPair: 3,
  OnePair: 2,
  HighCard: 1,
};

/**
 * Return the strongest hand category achievable from `cards` (up to 7 cards
 * for typical hold'em hole + board input). Requires at least 5 cards.
 */
export function evaluateHandCategory(cards: readonly Card[]): HandCategory {
  if (cards.length < 5) {
    throw new Error(`evaluateHandCategory: need ≥5 cards, got ${cards.length}`);
  }

  const ranks = rankCounts(cards);
  const suits = suitCounts(cards);

  const sortedRankCounts = Array.from(ranks.values()).sort((a, b) => b - a);
  const top = sortedRankCounts[0] ?? 0;
  const second = sortedRankCounts[1] ?? 0;

  const flushSuit = findFlushSuit(suits);
  const isFlush = flushSuit !== null;
  const rankValueSet = new Set(cards.map((c) => RANK_VALUE[c.rank]));
  const isStraight = containsStraight(rankValueSet);

  // Straight flush — straight using only cards of one suit
  if (isFlush && isStraight) {
    const suitedRanks = new Set(
      cards.filter((c) => c.suit === flushSuit).map((c) => RANK_VALUE[c.rank]),
    );
    if (containsStraight(suitedRanks)) return 'StraightFlush';
  }

  if (top === 4) return 'Quads';
  if (top === 3 && second === 2) return 'FullHouse';
  if (isFlush) return 'Flush';
  if (isStraight) return 'Straight';
  if (top === 3) return 'Set';
  if (top === 2 && second === 2) return 'TwoPair';
  if (top === 2) return 'OnePair';
  return 'HighCard';
}

/** Find a suit with 5+ cards; returns null if no flush is present. */
function findFlushSuit(suits: Map<Suit, number>): Suit | null {
  for (const [s, n] of suits.entries()) {
    if (n >= 5) return s;
  }
  return null;
}

/** True if some 5-card straight is entirely within `rankValues`. */
function containsStraight(rankValues: Set<number>): boolean {
  for (const high of STRAIGHTS_BY_HIGH) {
    const window = straightRanksFor(high);
    if (window.every((r) => rankValues.has(r))) return true;
  }
  return false;
}
